/**
 * OEM Live Reactions (OpenEye Marketing).
 * Shoppers tap 🔥 (or any emoji) on a product; everyone on the site sees it
 * burst in real time and the count tick up. Counts persist per product in the
 * relay's Durable Object storage (global "oem-reactions" room), so the tally
 * is the same on the homepage grid and the product page.
 *
 * Works on mobile and desktop (it's a tap either way).
 */

const MAX_BACKOFF_MS = 30_000;

class ReactionHub {
  #ws = null;
  #endpoint = null;
  #room = 'oem-reactions';
  #closed = false;
  #backoff = 1000;
  #queue = [];
  #counts = new Map(); // ref -> { emoji: n }
  #els = new Set(); // registered <oem-reaction> elements

  register(el) {
    this.#els.add(el);
    if (!this.#endpoint) this.#endpoint = el.dataset.endpoint;
    if (el.dataset.room) this.#room = el.dataset.room;
    // apply any count we already know
    this.#applyTo(el);
    this.#connect();
  }

  unregister(el) {
    this.#els.delete(el);
  }

  countFor(ref, emoji) {
    const c = this.#counts.get(ref);
    return c && c[emoji] ? c[emoji] : 0;
  }

  send(ref, emoji) {
    // optimistic local bump so the tapper sees instant feedback
    this.#bump(ref, emoji, this.countFor(ref, emoji) + 1);
    const msg = JSON.stringify({ t: 'react', id: 'r', ref, e: emoji });
    if (this.#ws && this.#ws.readyState === WebSocket.OPEN) this.#ws.send(msg);
    else this.#queue.push(msg);
  }

  #connect() {
    if (this.#ws || this.#closed) return;
    if (!this.#endpoint || !this.#endpoint.startsWith('wss://')) return;

    let socket;
    try {
      socket = new WebSocket(`${this.#endpoint.replace(/\/$/, '')}/${encodeURIComponent(this.#room)}`);
    } catch (e) {
      return;
    }
    this.#ws = socket;

    socket.addEventListener('open', () => {
      this.#backoff = 1000;
      socket.send(JSON.stringify({ t: 'hi', id: 'r' }));
      for (const m of this.#queue) socket.send(m);
      this.#queue = [];
    });

    socket.addEventListener('message', (event) => {
      let msg;
      try {
        msg = JSON.parse(event.data);
      } catch (e) {
        return;
      }
      if (!msg) return;
      if (msg.t === 'counts' && msg.counts) {
        for (const ref of Object.keys(msg.counts)) {
          this.#counts.set(ref, { ...(this.#counts.get(ref) || {}), ...msg.counts[ref] });
        }
        this.#els.forEach((el) => this.#applyTo(el));
        return;
      }
      if (msg.t === 'react' && msg.ref) {
        this.#bump(msg.ref, msg.e, msg.n, true);
      }
    });

    socket.addEventListener('close', () => {
      this.#ws = null;
      if (this.#closed) return;
      setTimeout(() => this.#connect(), this.#backoff);
      this.#backoff = Math.min(this.#backoff * 2, MAX_BACKOFF_MS);
    });

    socket.addEventListener('error', () => socket.close());
  }

  #bump(ref, emoji, n, burst = false) {
    const c = this.#counts.get(ref) || {};
    c[emoji] = Math.max(c[emoji] || 0, n);
    this.#counts.set(ref, c);
    this.#els.forEach((el) => {
      if (el.dataset.ref === ref && el.dataset.emoji === emoji) {
        el.setCount(c[emoji]);
        if (burst) el.burst();
      }
    });
  }

  #applyTo(el) {
    el.setCount(this.countFor(el.dataset.ref, el.dataset.emoji));
  }
}

const hub = new ReactionHub();

class OEMReaction extends HTMLElement {
  connectedCallback() {
    this.#button = this.querySelector('.oem-react__btn');
    this.#countEl = this.querySelector('[data-oem-react-count]');
    if (this.#button) {
      this.#button.addEventListener('click', this.#onClick, { passive: false });
    }
    hub.register(this);
  }

  disconnectedCallback() {
    hub.unregister(this);
  }

  #button = null;
  #countEl = null;

  #onClick = (event) => {
    // Don't let the card's overlay link fire when tapping the reaction.
    event.preventDefault();
    event.stopPropagation();
    hub.send(this.dataset.ref, this.dataset.emoji);
    this.burst();
  };

  setCount(n) {
    if (!this.#countEl) return;
    this.#countEl.textContent = n > 999 ? `${(n / 1000).toFixed(1)}k` : String(n);
    this.classList.toggle('oem-react--has', n > 0);
  }

  burst() {
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const fly = document.createElement('span');
    fly.className = 'oem-react__fly';
    fly.textContent = this.dataset.emoji;
    fly.style.left = `${20 + Math.random() * 30}%`;
    this.appendChild(fly);
    fly.addEventListener('animationend', () => fly.remove(), { once: true });
    if (this.#button) {
      this.#button.classList.remove('oem-react__btn--pop');
      void this.#button.offsetWidth;
      this.#button.classList.add('oem-react__btn--pop');
    }
  }
}

if (!customElements.get('oem-reaction')) customElements.define('oem-reaction', OEMReaction);
